import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ExternalLink, Loader2, Search, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const EMERALD = "bg-emerald-100 text-emerald-800 border-emerald-300";
const BLUE = "bg-blue-100 text-blue-800 border-blue-300";
const ROSE = "bg-rose-100 text-rose-800 border-rose-300";
const AMBER = "bg-amber-100 text-amber-800 border-amber-300";
const ZINC = "bg-zinc-100 text-zinc-500 border-zinc-300";
const SLATE = "bg-slate-100 text-slate-600 border-slate-300";

// v1 buckets (older triage rows) + the 19 policy categories (triage v2, Oct 2026)
export const CATEGORY_TONE = {
  "Payment": EMERALD, "Query": BLUE, "Legal notice": ROSE, "Invoice": AMBER, "Bounce / Spam": ZINC, "Other": SLATE,
  "Payment made": EMERALD, "Promise to pay": "bg-teal-100 text-teal-800 border-teal-300", "Arrangement request": "bg-cyan-100 text-cyan-800 border-cyan-300",
  "Settlement figure request": "bg-lime-100 text-lime-800 border-lime-300", "Settlement offer / discount": "bg-yellow-100 text-yellow-800 border-yellow-300",
  "Refund request": "bg-emerald-50 text-emerald-700 border-emerald-300",
  "Statement / document request": BLUE, "Figures query": "bg-sky-100 text-sky-800 border-sky-300", "Liability dispute": "bg-orange-100 text-orange-800 border-orange-300",
  "Account change": "bg-indigo-100 text-indigo-800 border-indigo-300", "Complaint": "bg-red-100 text-red-800 border-red-300",
  "Insolvency": "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300", "Supplier invoice": AMBER, "Internal": SLATE,
  "Auto-reply / bounce": ZINC, "Spam / marketing": ZINC, "Needs review": "bg-violet-100 text-violet-800 border-violet-300",
};

export const FLAG_TONE = {
  "Vulnerability": "bg-purple-600 text-white border-purple-600", "Hardship": "bg-amber-500 text-white border-amber-500",
  "Urgent": "bg-red-600 text-white border-red-600", "Broken promise": "bg-rose-600 text-white border-rose-600",
  "Creditor pressure": "bg-orange-500 text-white border-orange-500", "Fraud / dissipation": "bg-red-700 text-white border-red-700",
  "Reputational / legal threat": "bg-rose-700 text-white border-rose-700", "Abusive": "bg-zinc-800 text-white border-zinc-800",
  "Wants a call": "bg-sky-500 text-white border-sky-500", "Preferred contact": "bg-sky-100 text-sky-800 border-sky-300",
  "Won't deal with": "bg-slate-600 text-white border-slate-600",
};
export const flagTone = (f) => FLAG_TONE[f] || (String(f).startsWith("Won't deal with") ? FLAG_TONE["Won't deal with"] : "bg-slate-200 text-slate-800 border-slate-300");

export const STAGE_V2_LABEL = {
  current: "Current", arrears_l1: "Arrears L1", arrears_l2: "Arrears L2", arrears_l3: "Arrears L3", arrears_l4: "Arrears L4",
  pre_default: "Pre-Default · PDN issued", default: "Default · DN issued", legal: "Legal",
};
export const stageV2Tone = (s) => (s === "legal" || s === "default") ? "bg-rose-50 text-rose-800 border-rose-300" : s === "pre_default" ? "bg-orange-50 text-orange-800 border-orange-300"
  : String(s || "").startsWith("arrears") ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-white text-slate-600 border-slate-300";

export const HOW_LABEL = {
  sf_ref: "matched on the SF reference",
  sender_email: "matched on the sender's email",
  company_name: "matched on the company name",
  director_name: "matched on the director's name",
  loandisk_sf: "found in LoanDisk on the SF reference",
  loandisk_email: "found in LoanDisk on the sender's email",
  loandisk_name: "found in LoanDisk on the name",
  adopted: "file opened from LoanDisk",
  search: "picked by you",
};

export const stageLabel = (c) => {
  if (!c) return "";
  if (c.stage === "legal") return c.legal_stage ? `Legal · ${String(c.legal_stage).replace(/_/g, " ")}` : "Legal";
  return String(c.stage || "").replace(/_/g, " ") || "—";
};

export function CaseChip({ c, caseLink, onClear, testid = "case-chip" }) {
  if (!c) return null;
  return (
    <span className="inline-flex items-center gap-1.5 border rounded px-2 py-1 bg-indigo-50 border-indigo-200 text-xs max-w-full" data-testid={testid}>
      <span className="font-mono font-semibold text-indigo-900 shrink-0">{c.sf || "—"}</span>
      <span className="truncate text-indigo-900">{c.customer_name}</span>
      <Badge variant="outline" className="text-[9px] bg-white shrink-0">{stageLabel(c)}</Badge>
      {caseLink && (
        <Link to={caseLink(c)} target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-900 shrink-0" title="Open the customer file" data-testid={`${testid}-open`}>
          <ExternalLink className="w-3 h-3" />
        </Link>
      )}
      {onClear && (
        <button type="button" onClick={onClear} className="text-indigo-400 hover:text-indigo-800 shrink-0" title="Pick a different file" data-testid={`${testid}-clear`}>
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
}

// Pick the customer file an email belongs to — suggested match first, search otherwise.
export function CasePicker({ api, value, onChange, suggested, caseLink }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [searching, setSearching] = useState(false);
  useEffect(() => { if (!value && suggested) onChange(suggested); }, [suggested]); // eslint-disable-line react-hooks/exhaustive-deps

  const search = async () => {
    if (q.trim().length < 2) return;
    setSearching(true);
    try {
      const r = await api.searchCases(q.trim());
      setRows(r.matches || []);
      if ((r.matches || []).length === 1) onChange(r.matches[0]);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSearching(false); }
  };

  return (
    <div className="space-y-1.5" data-testid="case-picker">
      {value ? (
        <div className="flex items-center gap-2 flex-wrap">
          <CaseChip c={value} caseLink={caseLink} onClear={() => { onChange(null); setRows([]); }} testid="case-picker-chip" />
          {value.how && value.how !== "search" && <span className="text-[10px] text-muted-foreground">{HOW_LABEL[value.how]}</span>}
        </div>
      ) : (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded p-1.5" data-testid="case-picker-none">
          No customer file matched — search by SF number, company or director below.
        </div>
      )}
      {!value && rows.length > 0 && (
        <div className="border rounded divide-y max-h-36 overflow-y-auto bg-white">
          {rows.map(m => (
            <button key={m.case_id} type="button" onClick={() => onChange(m)}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-slate-50 text-left" data-testid={`case-picker-opt-${m.case_id}`}>
              <span className="font-mono font-semibold">{m.sf || "—"}</span>
              <span className="truncate flex-1">{m.customer_name}</span>
              <Badge variant="outline" className={cn("text-[9px]", m.stage === "legal" && "bg-rose-50 text-rose-700 border-rose-300")}>{stageLabel(m)}</Badge>
            </button>
          ))}
        </div>
      )}
      {!value && (
        <div className="flex gap-1">
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Search SF-27512, company or director" className="h-7 text-xs bg-white" data-testid="case-picker-search" />
          <Button size="sm" variant="outline" className="h-7 px-2" disabled={searching || q.trim().length < 2} onClick={search} data-testid="case-picker-search-btn">
            {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
          </Button>
        </div>
      )}
    </div>
  );
}
